import { Button } from '@/client/components/ui/button';
import { getMonthRange, getMonthWeekBuckets } from '@/client/lib/dates';
import { trpc } from '@/client/utils/trpc';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

function getGreeting(name?: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return `Good ${part}${name ? `, ${name.split(' ')[0]}` : ''}`;
}

export function UserDashboard() {
  const [monthOffset, setMonthOffset] = useState(0);

  const { data: me } = trpc.user.me.useQuery();
  const range = getMonthRange(monthOffset);
  const [yearStr, monthStr] = range.from.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);

  const { data: entries = [], isLoading } = trpc.timesheets.listEntries.useQuery({
    date_from: range.from,
    date_to: range.to,
  });

  const buckets = getMonthWeekBuckets(year, month);
  const totalHours = entries.reduce((s, e) => s + e.hours, 0);

  const weekRows = buckets.map((b) => ({
    label: b.label,
    hours: entries
      .filter((e) => e.date >= b.from && e.date <= b.to)
      .reduce((s, e) => s + e.hours, 0),
  }));

  return (
    <div className="mx-auto max-w-[640px] space-y-7">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border px-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonthOffset((o) => o - 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[130px] text-center text-sm font-medium">{range.label}</span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setMonthOffset((o) => o + 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="ml-auto">
          <Link to="/timesheets">
            <Button variant="outline" size="sm">
              Open timesheets →
            </Button>
          </Link>
        </div>
      </div>

      <div className="border-b pb-6">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {getGreeting(me?.full_name ?? undefined)} · {range.label}
        </p>
        <h1 className="mt-2 text-[38px] font-semibold leading-[1.08] tracking-[-0.025em]">
          {isLoading ? '—' : `${totalHours.toFixed(1)}h`}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">hours logged this month</p>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <div className="border-b px-5 py-3.5">
          <p className="text-sm font-semibold">Week breakdown</p>
        </div>
        {weekRows.map((row) => {
          const pct = Math.min((row.hours / 40) * 100, 100);
          const atTarget = row.hours >= 40;
          return (
            <div
              key={row.label}
              className="grid grid-cols-[160px_1fr_52px] items-center gap-4 border-b px-5 py-3 last:border-0"
            >
              <span className="text-sm text-muted-foreground">{row.label}</span>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full rounded-full ${atTarget ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span
                className={`tnum text-right text-sm font-semibold ${atTarget ? 'text-good' : ''}`}
              >
                {row.hours > 0 ? `${row.hours.toFixed(1)}h` : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
